import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { View, Text, Image, StyleSheet, Pressable } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';

import { Avatar, PlusView, I18n, Navigation, ArrowRight } from '../components';

import { getCurrentCaseWithFiles } from '../cases/selector';
import { openCaseInfo } from '../cases/actions';

import InviteButton from '../invite/invite_button';

const CALENDAR_ICON = require('../../../resources/nav/icon-calendar.png');
const MESSAGES_ICON = require('../../../resources/nav/icon-messages.png');
const HOME_ICON = require('../../../resources/nav/icon-home.png');
import { HOME_TAB, CALENDAR_TAB, MESSAGING_TAB } from '../../constants/tabs';

import { MainNavItem } from './sidebar_nav_item';

export default memo(function SideBarConsumer(props) {
  const dispatch = useDispatch();

  const currentCaseObj = useSelector(getCurrentCaseWithFiles);
  const currentCaseId = currentCaseObj?.caseId;
  const noCases = !currentCaseId;

  const openCaseForEdit = useCallback(() => {
    dispatch(openCaseInfo(currentCaseId));
    Navigation.showModal({
      screen: 'CaseInfo',
    });
  }, [dispatch, currentCaseId]);

  const selectCase = useCallback(() => {
    Navigation.showModal({
      screen: 'SelectCase',
      title: I18n.ac('case.activeCases'),
    });
  }, []);

  const newCase = useCallback(() => {
    Navigation.showModal({
      screen: 'CaseForm',
      title: I18n.ac('case.newCase'),
    });
  }, []);

  const navItems = useMemo(() => {
    const items = [
      {
        label: 'Home',
        icon: HOME_ICON,
        screenConfig: { screen: 'Home', nativeTab: HOME_TAB },
        selected: props.currentTab === 'Home',
      },
      {
        label: 'Calendar',
        icon: CALENDAR_ICON,
        screenConfig: { screen: 'Calendar', nativeTab: CALENDAR_TAB },
        selected: props.currentTab === 'Calendar',
      },
      {
        label: 'Messages',
        icon: MESSAGES_ICON,
        screenConfig: { screen: 'Messages', nativeTab: MESSAGING_TAB },
        selected: props.currentTab === 'Messages',
      },
    ];
    return items;
  }, [props.currentTab]);

  return (
    <View style={styles.mainNav}>
      <View style={styles.casesButtonsSection}>
        <Pressable
          style={[styles.button, styles.headerButton, { flex: 1 }]}
          onPress={selectCase}
          disabled={!!noCases}
        >
          <Image
            style={[styles.buttonIcon, styles.headerButtonIcon]}
            source={require('../../../resources/icon-cases.png')}
            resizeMode="contain"
          />
          <Text
            style={[
              styles.headerButtonText,
              { fontSize: 18 },
              noCases ? styles.textDisabled : null,
            ]}
          >
            {I18n.c1('userProfile.cases')}
          </Text>
        </Pressable>

        <Pressable style={styles.addCaseButton} testID="addCaseButton" onPress={newCase}>
          <Text style={styles.headerButtonText}>{I18n.t('case.addCase')}</Text>
          <PlusView style={styles.plusButton} />
        </Pressable>
      </View>

      <View style={styles.casesHeader}>
        <Text style={[styles.subheadText, noCases ? styles.textDisabled : null]}>
          {I18n.ac('case.activeCase')}
        </Text>
      </View>
      <View style={styles.activeCaseContainer}>
        {!!noCases ? (
          <View style={{ height: 70, justifyContent: 'center' }}>
            <Text style={[THEME.textRegular, { margin: 15, marginLeft: 25 }]}>
              {I18n.t('case.noActiveCase')}
            </Text>
          </View>
        ) : (
          <Pressable onPress={openCaseForEdit} style={styles.editCaseButton}>
            <Avatar
              initialsStr={currentCaseObj.name}
              onPress={null}
              attachment={currentCaseObj.avatar}
              circleStyles={styles.caseAvatarStyle}
            />
            <Text style={styles.caseNameText}>{currentCaseObj.name}</Text>

            <View style={{ paddingBottom: 5 }}>
              <ArrowRight />
            </View>
          </Pressable>
        )}
      </View>
      <InviteButton
        enabled={!noCases}
        buttonStyles={{ paddingLeft: 25 }}
        iconStyles={styles.inviteButtonStyle}
        label={I18n.t('case.inviteOthersToCase')}
      />

      {__WEB__ && (
        <View style={styles.bottomNav}>
          {navItems.map((item) => (
            <MainNavItem key={item.label} {...item} onPress={props.openScreen} />
          ))}
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  mainNav: {
    paddingTop: 50,
  },
  bottomNav: {
    backgroundColor: COLOR.labelText,
  },
  casesButtonsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 25,
    // paddingHorizontal: 25,
  },
  addCaseButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 10,
  },
  plusButton: {
    width: 18,
    height: 18,
    borderRadius: 3,
    marginLeft: 10,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 25,
    paddingVertical: 10,
  },
  headerButton: {
    paddingVertical: 15,
  },
  buttonIcon: {
    marginRight: 20,
    tintColor: COLOR.textLight,
  },
  headerButtonIcon: {
    width: 22,
    height: 22,
  },
  headerButtonText: {
    ...FONT.semibold,
    fontSize: 16,
    color: COLOR.text,
  },
  textDisabled: {
    color: COLOR.textDisabled,
  },
  iconDisabled: {
    tintColor: COLOR.textDisabled,
  },

  casesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e7e7e7',
    height: 55,
    paddingHorizontal: 25,
    marginTop: 20,
    borderColor: '#e4e4e4',
    borderBottomWidth: 1,
  },

  subheadText: {
    flex: 1,
    ...FONT.semibold,
    fontSize: FONT_SIZE.regular,
    color: COLOR.textLight,
  },
  activeCaseContainer: {
    backgroundColor: COLOR.altContrast,
    borderColor: '#e4e4e4',
    borderBottomWidth: 2,
    marginBottom: 20,
  },
  editCaseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 16,
    paddingRight: 25,
    paddingVertical: 7,
    paddingTop: 20,
  },
  caseNameText: {
    flex: 1,
    ...THEME.textSemibold,
    fontSize: __WEB__ ? 16 : 20,
    color: COLOR.text,
    marginLeft: 10,
    marginBottom: 7,
  },
  caseAvatarStyle: {
    width: 35,
    height: 35,
  },
  inviteButtonStyle: {
    width: 30,
    height: 30,
    tintColor: COLOR.text,
  },
});
